import {DatapointDependencyMap, KpiCategory, KpiDefinition, KpiDependencyMap, KpiStructure} from "./kpi-definition";
import ProjectSection from "./project-section";

export type NestedData = {
  [key: string]: NestedData | string[]; // Each key can either lead to another nested object or an array of strings
};

export type KpisToSectionsAssignments = { [sectionId: string]: string[] }

export class KpiStructureDependencyFinder {
  private readonly kpiData: KpiStructure;
  private readonly kpis: KpiDefinition[] = [];
  private readonly kpiDependencyMap: KpiDependencyMap = {};
  private readonly datapointDependencyMap: DatapointDependencyMap = {};
  private isDependencyAnalysed = false;
  public static readonly dynamicKeyName = "dynamicitemid";
  public static readonly dynamicKeyPlaceholder = "${" + KpiStructureDependencyFinder.dynamicKeyName + "}";

  constructor(kpiData: KpiStructure) {
    this.kpiData = kpiData;
  }

  private collectAllKPIs(categories: KpiCategory[], variables: { [varName: string]: string }) {
    categories?.forEach((category) => {
      if (category.kpiDefinitions) {
        for( const kpiDefinition of category.kpiDefinitions ) {
          this.kpis.push({
            ...kpiDefinition,
            id: KpiStructureDependencyFinder.imposeNameVariables(kpiDefinition.id, variables),
            name: KpiStructureDependencyFinder.imposeNameVariables(kpiDefinition.name, variables),
            calculationPhrase: KpiStructureDependencyFinder.imposeNameVariables(kpiDefinition.calculationPhrase, variables),
          });
        }
        
      }

      if (category.subCategories) {
        this.collectAllKPIs(category.subCategories, {});
      }
      
      if(category.dynamicKpiCategories) {
        category.dynamicKpiCategories.forEach((dynamicCategory) => {
          const kpiForDynamicQuestionnaire = this.kpiData.dynamicKpis?.find(dk => dk.questionnaireId === dynamicCategory.questionnaireId);

          if(!kpiForDynamicQuestionnaire) {
            throw Error(`KPI definitions for dynamic questionnaire ${dynamicCategory.questionnaireId} not found.`);
          }

          this.collectAllKPIs(kpiForDynamicQuestionnaire.categories, { "sectionId": dynamicCategory.sectionId });
        })
      }
    });
  }
  
  public static imposeNameVariables(name: string, variables: { [varName: string]: string }): string {
    if(variables) {
      for(const varName of Object.keys(variables)) {
        const regEx = new RegExp("\\$\\{" + varName + "\\}", "ig");
        name = name.replace(regEx, variables[varName]);
      }
    }
    
    return name;
  }
  
  public static isKeyDynamic(key: string | undefined): boolean {
    return key != null && key.includes(KpiStructureDependencyFinder.dynamicKeyPlaceholder);
  }
  
  public static getDynamicKeyResolved(key: string, value: string): string {
    return KpiStructureDependencyFinder.imposeNameVariables(key, {[KpiStructureDependencyFinder.dynamicKeyName]: value });
  }

  static getDynamicNestedDataResolved(data: NestedData, dynamicItemIds: string[], projectSections: ProjectSection[]) {
    const result: NestedData = {};

    for(const key of Object.keys(data)) {
      if(KpiStructureDependencyFinder.isKeyDynamic(key) && dynamicItemIds.length > 0)
      {
        dynamicItemIds.forEach(e => {
          const resolvedKey = KpiStructureDependencyFinder.getDynamicKeyResolved(key, e);
          result[resolvedKey] = KpiStructureDependencyFinder.getDynamicArrayResolved(data[key] as string[], [e])
        });
      } else {
        const newDynamicItems = projectSections.find(ps => ps.id === key)?.dynamicItems ?? [];

        if(Array.isArray(data[key])) {
          result[key] = KpiStructureDependencyFinder.getDynamicArrayResolved(data[key] as string[], newDynamicItems)
        } else {
          result[key] = KpiStructureDependencyFinder.getDynamicNestedDataResolved(data[key] as NestedData, newDynamicItems, projectSections)
        }

      }
    }

    return result;
  }

  static getDynamicArrayResolved(data: string[], dynamicItemIds: string[]) {
    if(!data || data.length === 0 || !dynamicItemIds || dynamicItemIds.length === 0) {
      return data;
    }
    
    const result: string[] = [];

    for(const element of data) {
      if(KpiStructureDependencyFinder.isKeyDynamic(element) && dynamicItemIds.length > 0)
      {
        dynamicItemIds.forEach(e => {
          result.push(KpiStructureDependencyFinder.getDynamicKeyResolved(element, e));
        });
      } else {
        result.push(element);
      }
    }

    return result;
  }
  
  static getDynamicKpisToSectionsAssignmentsResolved(kpisToSectionsAssignment: KpisToSectionsAssignments, projectSections: ProjectSection[] | undefined) {
    if(!kpisToSectionsAssignment || !projectSections || projectSections.length === 0) {
      return kpisToSectionsAssignment;
    }
    
    const result: KpisToSectionsAssignments = {};
    
    for(const key of Object.keys(kpisToSectionsAssignment)) {
      const dynamicItems = projectSections.find(ps => ps.id === key)?.dynamicItems ?? [];

      result[key] = KpiStructureDependencyFinder.getDynamicArrayResolved(kpisToSectionsAssignment[key], dynamicItems)
    }
    
    return result;
  }

  // Function to recursively analyze kpiDependencyMap and collect data field kpiDependencyMap
  private collectFieldAndKPIDependencies(kpi: KpiDefinition, visited = new Set<string>(), currentPath: string[] = []) {
    if (!kpi.calculationPhrase || visited.has(kpi.id)) {
      return;
    }

    visited.add(kpi.id);
    currentPath.push(kpi.id);

    const fieldMatches = kpi.calculationPhrase.match(/\[([a-zA-Z0-9_!${}@]+)\]/g);

    if (fieldMatches) {
      fieldMatches.forEach((match) => {
        const field = match.slice(1, -1);
        if (!this.kpiDependencyMap[field]) {
          this.kpiDependencyMap[field] = new Set<string>();
        }

        currentPath.forEach((dependentKpiId) => this.kpiDependencyMap[field].add(dependentKpiId));

        // Check if the field is actually another KPI
        const dependentKpi = this.kpis.find((otherKpi) => otherKpi.id === field);
        if (dependentKpi) {
          this.collectFieldAndKPIDependencies(dependentKpi, visited, currentPath);
        } else {
          if (!this.datapointDependencyMap[kpi.id]) {
            this.datapointDependencyMap[kpi.id] = new Set();
          }
          this.datapointDependencyMap[kpi.id].add(field);
        }
      });
    }

    visited.delete(kpi.id);
    currentPath.pop();
  }

  // Function to create a list of fields and the KPIs that depend on them
  private analyzeFieldAndKPIDependencies() {
    this.kpis.forEach((kpi) => {
      if (kpi.calculationPhrase) {
        this.collectFieldAndKPIDependencies(kpi);
      }
    });

    // Convert the kpiDependencyMap object from Sets to Arrays for readability
    const formattedDependencies: { [dataPointId: string]: string[] } = {};

    for (const field in this.kpiDependencyMap) {
      formattedDependencies[field] = Array.from(this.kpiDependencyMap[field]);
    }

    return formattedDependencies;
  }
  
  private analyzeDependencies(): void {
    this.collectAllKPIs(this.kpiData.categories, {});
    this.analyzeFieldAndKPIDependencies();
    
    this.isDependencyAnalysed = true;
  }

  public getFieldDependencies(): NestedData {
    if(!this.isDependencyAnalysed) {
      this.analyzeDependencies();
    }

    const nestedData = this.transformToNestedStructure(this.kpiDependencyMap);

    return nestedData;
  }
  
  public getKpiToSectionsAssignment(): KpisToSectionsAssignments {
    if(!this.isDependencyAnalysed) {
      this.analyzeDependencies();
    }
    
    const result: KpisToSectionsAssignments = {};

    Object.keys(this.datapointDependencyMap).forEach((key) => {

      const dependentSections = new Set([...this.datapointDependencyMap[key]].map((datapointKey) => {
        const keyParts = datapointKey.split("!"); // Split the key by the "!" character
        return keyParts[0];
      }));
      
      if(dependentSections.size === 1) {
        const sectionName = [...dependentSections][0];
        if (result[sectionName] === undefined) {
          result[sectionName] = [];
        }
        result[sectionName].push(key);
      }
    });
    
    return result;
  }

  private transformToNestedStructure(flatData: KpiDependencyMap): NestedData {
    const nestedData: NestedData = {};

    Object.keys(flatData).forEach((key) => {
      let keyParts = key.split("!"); // Split the key by the "!" character
      
      if(keyParts.length > 2) {
        const p1 = keyParts[0];
        const p2 = keyParts.slice(1).join('!');
        keyParts = [p1, p2];
      }
      
      let currentLevel: NestedData = nestedData;

      // Iterate through key parts to build the nested structure
      keyParts.forEach((part, index) => {
        if (index === keyParts.length - 1) {
          // If it's the last part, assign the value
          currentLevel[part] = Array.from(flatData[key]);
        } else {
          // If the key part does not exist, create an empty object
          if (!currentLevel[part]) {
            currentLevel[part] = {};
          }
          currentLevel = currentLevel[part] as NestedData; // Go one level deeper
        }
      });
    });

    return nestedData;
  }
}
